import React, { useState, useEffect } from 'react';
import Layout from 'src/components/common/theme/Layout';
import { css, useTheme } from 'styled-components';
import { SlickSlider } from 'src/components/common/modules/carousel/slick/slick-carousel';
import { getDeviceImage, ImageCard } from 'src/components/common/theme/component';
import { checkValue, getDefaultValue, LightenDarkenColor } from 'src/components/common/helper';
import { Typo, Sticker } from 'src/components/common/ui';
import { UitkButtonSize } from 'uitk-react-button';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import { LazyImage } from 'src/components/common/modules/lazy/lazy-image';
import { StickerProps } from 'src/components/common/module-util';
import { CopyrightCameraProps } from 'src/components/common/modules/copyright-with-gradient/code-split/copyright-with-gradient-component';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import MagicSliderDots from 'react-magic-slider-dots';
import * as config from 'src/config/config';

// Css Style Import
import(/* webpackChunkName: "v1-content-tile-styles" */ 'src/components/common/modules/v1/content-tile/styles/content-tile.css');

const gaTracking = {
  eventCategory: 'Slick carousel',
  eventAction: 'Content Tile - Carousel',
  eventLabel: 'slide-',
};

const ContentTileV2 = (props: any) => {
  const {
    data: {
      headline,
      galleryTileSize = 'Small',
      galleryItems,
      backgroundColor,
      backgroundRectangleBarColor,
      stickerImage,
      stickerPosition,
      headlineStyle,
      cTAText: commonCTAText,
      cTAUrl: commonCTAUrl,
      openUrlInNewTab: commonCTAOpenUrlInNewTab,
      subHeadline,
      cTATextColor,
      cTAColor,
      galleryItemsTextStyle,
      textColor,
      galleryStyle,
    },
    id,
  } = props;

  const theme = useTheme();
  const fontWeight = theme?.styles?.fontWeight;
  const isCommonButton = checkValue(commonCTAText) && checkValue(commonCTAUrl);
  const isStyle2 = checkValue(galleryStyle) && galleryStyle === 'Style-2';
  const isStyle3 = checkValue(galleryStyle) && galleryStyle === 'Style-3';
  const isStyle4 = checkValue(galleryStyle) && galleryStyle === 'Style-4';

  const settings = {
    arrows: true,
    slidesToShow: 3,
    slidesToScroll: 3,
    infinite: galleryItems?.length > 3,
    dots: true,
    className: 'center content-tile-v2-sldier',
    centerMode: galleryItems?.length > 3,
    centerPadding: '1px',
    responsive: [
      {
        breakpoint: 575,
        settings: {
          centerMode: isStyle2,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          infinite: false,
          dots: true,
          centerPadding: isStyle2 ? '20px' : '0px',
          appendDots: (dots) => {
            return <MagicSliderDots dots={dots} numDotsToShow={3} dotWidth={22} />;
          },
        },
      },
    ],
  };

  const [device, setDevice] = useState('desktop');
  useEffect(() => {
    let device: string;
    device = 'desktop';
    if (document !== undefined && document.body !== undefined) {
      if (document.body.offsetWidth > 1024) {
        device = 'desktop';
      } else if (document.body.offsetWidth > 575) {
        device = 'tablet';
      } else {
        device = 'mobile';
      }
    }
    setDevice(device);
  }, []);

  const [loadedClass, setLoadedClass] = useState('');

  const updateLoaded = (isLoaded) => {
    if (isLoaded) {
      setLoadedClass('image-loaded');
    } else {
      setLoadedClass('');
    }
  };

  const imageCropSize = () => {
    const displaySize = galleryTileSize.replace(/\s+/g, '-').toLowerCase();
    if (displaySize === 'small') {
      return 'tile-image-medium';
    }
    if (displaySize === 'large') {
      return 'tile-image-large';
    }
    return 'tile-image-medium';
  };

  const primaryButtonProps: PrimaryButtonProps = {
    text: 'Book Now',
    tag: 'button',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'featured Gallery Item',
      eventLabel: 'Book Now',
    },
    exClass: `btn mobile-btn`,
    size: UitkButtonSize.LARGE,
  };

  const tileBody = (tileTitle: string, tileDescription: string, subHeadline: string, isStyle2: boolean) => {
    return (
      <>
        {checkValue(subHeadline) && (
          <Typo.HTMLText tag="p" exClass={`featured-gallery-body-text body-subtitle`}>
            {subHeadline}
          </Typo.HTMLText>
        )}
        {checkValue(tileTitle) && (
          <Typo.HTMLText tag="p" exClass={`txtBold featured-gallery-body-text ${isStyle2 ? 'style-2-title' : ''}`}>
            {tileTitle}
          </Typo.HTMLText>
        )}
        {checkValue(tileDescription) && (
          <div
            className={`cms-typo featured-gallery-body-text ${isStyle2 ? 'style-2-description' : ''}`}
            dangerouslySetInnerHTML={{ __html: tileDescription }}
          />
        )}
      </>
    );
  };

  const contentTiles = () => {
    if (checkValue(galleryItems) && typeof galleryItems !== 'string') {
      return galleryItems?.map((content: any, index: number) => {
        const {
          headline,
          subHeadline,
          summary,
          image,
          imageDescription = '',
          rectangleBarColor,
          cTAText,
          cTALink,
          openUrlInNewTab,
          imageCopyright,
          uniquekey,
        } = content;
        const contentImag = checkValue(image)
          ? getDeviceImage(image, imageCropSize())
          : config.config.staticPlaceholderImage;

        const copyrightCameraProps: CopyrightCameraProps = {
          copyrightText: imageCopyright,
          position: 'br',
          iconSize: 'sm',
        };

        if (checkValue(contentImag)) {
          const contentTitle = checkValue(headline) ? headline : '';
          const contentDescription = checkValue(summary) ? summary : '';
          const clickEventTarget = checkValue(openUrlInNewTab) && openUrlInNewTab === 'True' ? '_blank' : '_self';
          const contentContentUrl = '';
          const imageAltText = getDefaultValue(imageDescription, `Content Tile Image ${index + 1}`);
          const linkProps = checkValue(cTALink)
            ? {
                href: cTALink,
                target: clickEventTarget,
                rel: cTALink ? 'noreferrer' : '',
              }
            : {};

          return (
            <div
              key={uniquekey}
              className={`
              content-tile-wrapper
              ${galleryItems.length < 4 ? 'no-carousal-view' : ''}
              ${galleryItems.length < 4 && index === 1 ? 'no-carousal-view-middle' : ''}
              ${checkValue(galleryItemsTextStyle) ? galleryItemsTextStyle.toLowerCase() : ''}
            `}
            >
              <a className={`content-tile-link ${!cTALink ? 'content-tile-no-link' : ''}`} {...linkProps}>
                <React.Fragment key={`content-tile-card-${index}`}>
                  <>
                    <div>
                      <div className={`image-wrapper ${loadedClass}`}>
                        <LazyImage updateLoaded={updateLoaded} isGallery={true} device={device}>
                          {ImageCard(
                            contentImag,
                            contentTitle,
                            contentContentUrl,
                            clickEventTarget,
                            'ga4-content-tile-card',
                            {
                              eventCategory: 'Button Click',
                              eventAction: 'Content Tile - Card',
                              eventLabel: `${contentTitle}`,
                            },
                            imageAltText,
                            ['all'],
                            null,
                            checkValue(rectangleBarColor)
                              ? {
                                  color: rectangleBarColor,
                                  position: imageCropSize() === 'tile-image-large' ? 'top' : 'bottom',
                                }
                              : null,
                            copyrightCameraProps,
                            checkValue(isStyle3) || checkValue(isStyle4)
                              ? UitkFigureAspectRatioType.R1_1
                              : UitkFigureAspectRatioType.R16_9
                          )}
                        </LazyImage>
                      </div>
                      {tileBody(contentTitle, contentDescription, subHeadline, isStyle2)}
                    </div>
                    {!checkValue(isCommonButton) && checkValue(cTAText) && checkValue(cTALink) ? (
                      <>
                        {isStyle2 ? (
                          <div className="content-link-container">
                            <a className="content-link" href={cTALink} target={clickEventTarget}>
                              {`${cTAText}`}
                              <span>{'>'}</span>
                            </a>
                          </div>
                        ) : (
                          <div className="content-button-container" data-url={cTALink}>
                            <PrimaryButton
                              {...{
                                ...primaryButtonProps,
                                ...{
                                  text: cTAText,
                                  url: cTALink,
                                  target: clickEventTarget,
                                },
                              }}
                            />
                          </div>
                        )}
                      </>
                    ) : null}
                  </>
                </React.Fragment>
              </a>
            </div>
          );
        } else {
          return null;
        }
      });
    } else {
      return null;
    }
  };

  const moduleCss = () => {
    const getBackgroundColor = checkValue(backgroundColor) ? backgroundColor : '';
    const getBackgroundRectangleBarColor = checkValue(backgroundRectangleBarColor) ? backgroundRectangleBarColor : '';
    const getheadlinestyle = checkValue(headlineStyle) ? headlineStyle : 'left';
    const darkenColor = checkValue(cTAColor) ? LightenDarkenColor(cTAColor, -0.1) : '';

    return `
      ${
        checkValue(fontWeight)
          ? `
        .content-tile-v2 .content-tile-wrapper .cms-typo.txtBold {
          font-weight: ${fontWeight?.w7} !important;
        }
        `
          : ''
      }
      ${
        checkValue(getBackgroundColor)
          ? `
          background-color: ${getBackgroundColor} !important;
        `
          : ''
      }
      ${
        checkValue(getBackgroundRectangleBarColor)
          ? `
        .background-stripe {
          background-color: ${getBackgroundRectangleBarColor} !important;
        }
        `
          : ''
      }
 
      ${
        checkValue(getheadlinestyle)
          ? `
        .content-title-v2-title{
          text-align: ${getheadlinestyle} !important;
        } 
        .content-title-v2-sub-title{
          text-align: ${getheadlinestyle} !important;
        }
        `
          : ''
      }

      ${
        checkValue(cTAColor)
          ? `
        .featured-gallery-carousel-container{
          .content-button-container{
            .btn {
              background-color:${cTAColor}!important;
              border: 1px solid ${cTAColor}!important;

              &:hover {
                background-color:${darkenColor}!important;
                border: 1px solid ${darkenColor}!important;
              }
            }
          }
          .common-content-panel-cta-container{
            .common-content-panel-cta-button{
              background-color:${cTAColor}!important;
              border: 1px solid ${cTAColor}!important;

              &:hover {
                background-color:${darkenColor}!important;
                border: 1px solid ${darkenColor}!important;
              }
            }
          }
        }
      `
          : ''
      }

      ${
        checkValue(cTATextColor)
          ? `
        .featured-gallery-carousel-container{
          .content-button-container{
            .btn {
              color:${cTATextColor}!important;
            }
          }
          .common-content-panel-cta-container{
            .common-content-panel-cta-button{
              color:${cTATextColor}!important;
            }
          }
        }
      `
          : ''
      }
      ${
        checkValue(textColor)
          ? `
        .featured-gallery-body-text {
          color: ${textColor} !important;
        }
        .slick-slider .slick-arrow:before {
          border-color: ${textColor};
        }
        .content-tile-v2-sldier .slick-dots li button:before {
          color: ${textColor} !important;
        }
        .content-tile-v2-sldier .slick-dots .slick-active button:before {
          color: ${textColor} !important;
        }
        .content-link {
          color: ${textColor} !important;
        }
        .content-title-v2-title {
          color: ${textColor} !important;
        }
        .content-title-v2-sub-title {
          color: ${textColor} !important;
        }
      `
          : ''
      }
    `;
  };

  const tiles = contentTiles();

  const stickerProps: StickerProps = {
    stickerImage,
    stickerPosition,
    exClass: `content-tile-v2-sticker-container ${!checkValue(headline) ? 'max-size' : ''}`,
  };

  const ga = {
    eventCategory: 'Button Click',
    eventAction: 'featured Gallery common button',
    eventLabel: '',
  };

  const commonCTAProps: PrimaryButtonProps = {
    text: '',
    tag: 'a',
    gaTracking: ga,
    exClass: `btn common-content-panel-cta-button`,
    url: '',
    target: '',
    size: UitkButtonSize.LARGE,
  };

  const contentTileMainContent = () => (
    <div
      className={`featured-gallery-carousel-container content-tile-v2__slider ${
        checkValue(galleryTileSize)
          ? 'content-tile-v2__slider--' + galleryTileSize.replace(/\s+/g, '-').toLowerCase()
          : 'content-tile-v2__slider--small'
      }`}
    >
      {checkValue(tiles) && (
        <>
          <Sticker {...stickerProps} />
          {isStyle3 || (isStyle4 && device !== 'mobile') ? (
            <div className="content-tile-v2__grid">{tiles}</div>
          ) : (
            <SlickSlider settings={settings} gaTracking={gaTracking}>
              {tiles}
            </SlickSlider>
          )}
          {isCommonButton ? (
            <div className="common-content-panel-cta-container" data-url={commonCTAUrl}>
              <PrimaryButton
                {...{
                  ...commonCTAProps,
                  ...{
                    text: commonCTAText,
                    url: commonCTAUrl,
                    target:
                      checkValue(commonCTAOpenUrlInNewTab) && commonCTAOpenUrlInNewTab === 'True' ? '_blank' : '_self',
                  },
                }}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <>
      {checkValue(tiles) && (
        <Layout
          className={`content-tile-v2-section ${isStyle2 ? 'variant2' : ''} ${
            isStyle3 || isStyle4 ? 'variant3 variant3--four-grid' : ''
          } ${isStyle4 ? 'variant4' : ''}`}
          css={css`
            ${moduleCss()}
          `}
          id={id}
        >
          <div
            className={`content-tile content-tile-v2 container-fluid content-tile-v2-component-spacing
            ${isCommonButton ? 'content-tile-v2-component-spacing-with-common-button' : ''}
            ${galleryItems?.length > 3 && isStyle2 ? 'style-2-slider-spacing' : ''}
            `}
          >
            <div>
              {checkValue(headline) && (
                <Typo.HTMLText exClass="content-title-v2-title override-heading-font-family" tag="h2">
                  {headline}
                </Typo.HTMLText>
              )}
              {checkValue(subHeadline) && (
                <Typo.HTMLText exClass="content-title-v2-sub-title" tag="p">
                  {subHeadline}
                </Typo.HTMLText>
              )}
              {contentTileMainContent()}
            </div>
          </div>
        </Layout>
      )}
    </>
  );
};

export default ContentTileV2;
